import type { Bcp47Locale, Market } from '@backmarket/http-api'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'

import { isPaymentMethodEnabled } from '../../methods'
import type { PaymentAdvertisementOptions } from '../types/PaymentAdvertisementOptions'
import type { PaymentAdvertisementConfigBySpot } from '../types/PaymentAdvertisingConfig'
import { PaymentAdvertisingId } from '../types/PaymentAdvertisingId'
import { PaymentAdvertisingSpot } from '../types/PaymentAdvertisingSpot'

import { sortPaymentMethodsAdvertisement } from './sortPaymentMethodsAdvertisement'

/**
 * Selects the first advertising configuration (by priority) that matches:
 * - the provided {@link PaymentAdvertisingSpot spot}, and
 * - the currently available payment methods.
 *
 * Priority is market-dependent and comes from `sortPaymentMethodsAdvertisement(market)`,
 * which uses the advertising registry and optional country overrides.
 */
export function findAvailablePaymentAdvertisement<
  Spot extends PaymentAdvertisingSpot,
  Config = PaymentAdvertisementConfigBySpot[Spot],
>(
  spot: Spot,
  paymentMethods: PaymentMethod[],
  options: PaymentAdvertisementOptions & {
    market: Market
    locale: Bcp47Locale
  },
) {
  const methods = options?.includeDisabledMethods
    ? paymentMethods
    : paymentMethods.filter(isPaymentMethodEnabled)

  for (const [id, config] of sortPaymentMethodsAdvertisement(
    options.market,
    options.locale,
  )) {
    if (config[spot]) {
      const { isAvailable, forMethods } = config.isAvailableFor(methods)
      if (isAvailable) {
        return {
          id: id as PaymentAdvertisingId,
          methods: forMethods,
          config: config[spot] as Config,
        }
      }
    }
  }

  return null
}
